import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthService, GUEST_EMAIL_SUFFIX, JwtAuthenticationResponse } from './auth/auth';
import { RegistrationService } from './api/registration';

const GUEST_KEY = 'bl_guest';
const GUEST_ROLE_ID = 5; // ROLE_PUBLIC

interface GuestCredentials {
  email: string;
  password: string;
}

/**
 * Гость: публичный клиент считает без телефона. Сайт сам заводит ему запись с уникальным кодом (uuid) в логине
 * «uuid@guest.bilload.kz» — по нему запись можно найти в базе. Логин и пароль лежат в браузере, чтобы после
 * окончания токена (24 часа) войти заново без участия клиента. Телефон потом заменяет этот логин (attachPhone).
 */
@Injectable({ providedIn: 'root' })
export class GuestSessionService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private registration: RegistrationService
  ) {}

  /** Есть ли что использовать для тихого повторного входа гостя. */
  hasCredentials(): boolean {
    return this.read() !== null;
  }

  /** Гарантирует вход: уже вошёл — ничего, иначе входит под сохранённым гостем или заводит нового. */
  ensureSession(): Observable<void> {
    if (this.authService.isAuthenticated()) {
      return of(undefined);
    }
    const saved = this.read();
    if (saved) {
      return this.signIn(saved).pipe(
        // Сохранённого гостя не нашлось (запись удалена) — заводим нового
        catchError(() => this.createGuest())
      );
    }
    return this.createGuest();
  }

  /** Тихий повторный вход после окончания токена; false — гостя нет или войти не удалось. */
  reSignIn(): Observable<boolean> {
    const saved = this.read();
    if (!saved) {
      return of(false);
    }
    return this.signIn(saved).pipe(map(() => true), catchError(() => of(false)));
  }

  /**
   * Телефон вместо гостевого логина: бэкенд меняет логин (или переносит расчёты в уже известную запись).
   * Имя уходит вместе с телефоном — вместо «Гость», созданного при гостевом входе.
   */
  attachPhone(phone: string, fullName?: string): Observable<void> {
    return this.http.post<JwtAuthenticationResponse>('/cargo/api/users/current/phone', { phone, fullName }).pipe(
      tap(response => {
        this.authService.saveSession(response);
        this.clear();
      }),
      map(() => undefined)
    );
  }

  private createGuest(): Observable<void> {
    const creds: GuestCredentials = {
      email: `${this.uuid()}${GUEST_EMAIL_SUFFIX}`,
      password: this.uuid()
    };
    return this.registration.register({
      email: creds.email,
      fullName: 'Гость',
      password: creds.password,
      roleId: GUEST_ROLE_ID
    }).pipe(
      tap(() => this.write(creds)),
      switchMap(() => this.signIn(creds))
    );
  }

  private signIn(creds: GuestCredentials): Observable<void> {
    return this.authService.signIn({ username: creds.email, password: creds.password }).pipe(map(() => undefined));
  }

  private uuid(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  private read(): GuestCredentials | null {
    try {
      const raw = localStorage.getItem(GUEST_KEY);
      return raw ? (JSON.parse(raw) as GuestCredentials) : null;
    } catch {
      return null;
    }
  }

  private write(creds: GuestCredentials): void {
    try {
      localStorage.setItem(GUEST_KEY, JSON.stringify(creds));
    } catch {
      // без хранилища гость не сможет войти заново после окончания токена
    }
  }

  private clear(): void {
    try {
      localStorage.removeItem(GUEST_KEY);
    } catch {
      // игнорируем
    }
  }
}
